import * as XLSX from 'xlsx'
import { PRESETS } from './presets'
import { classifyDairyMarking } from './dairyMarking'
import type {
  ColumnMapping,
  ImportPreset,
  ParsedRow,
  ParseResult,
  TargetField
} from './types'

/** Ошибка парсинга с понятным сообщением для пользователя. */
export class ExcelParseError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ExcelParseError'
  }
}

/**
 * «Умный» парсер прайсов из Excel.
 *
 * 1. Читает первый лист книги в массив объектов (ключи = заголовки колонок).
 * 2. Авто-детектит пресет (Контур/СБИС/FusionPOS/1С) по заголовкам.
 * 3. Нормализует строки в унифицированный ParsedRow.
 *
 * Пресет можно задать принудительно, если авто-детект не подходит.
 */
export class ExcelParser {
  constructor(private readonly presets: ImportPreset[] = PRESETS) {}

  /** Распарсить книгу из ArrayBuffer (например, File.arrayBuffer()). */
  parse(data: ArrayBuffer, forcedPresetId?: string): ParseResult {
    const workbook = XLSX.read(data, { type: 'array' })
    const sheetName = workbook.SheetNames[0]
    if (!sheetName) {
      throw new ExcelParseError('Книга не содержит листов')
    }
    const sheet = workbook.Sheets[sheetName]
    const records = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
      defval: null
    })
    if (records.length === 0) {
      throw new ExcelParseError('Лист пуст — нет строк для импорта')
    }

    const headers = Object.keys(records[0] ?? {})
    const preset = forcedPresetId
      ? this.presets.find((p) => p.id === forcedPresetId)
      : this.detectPreset(headers)

    if (!preset) {
      throw new ExcelParseError(
        `Не удалось определить формат файла. Заголовки: ${headers.join(', ') || '(пусто)'}. ` +
          `Поддерживаются: ${this.presets.map((p) => p.label).join(', ')}.`
      )
    }

    const resolved = this.resolveColumns(headers, preset.mapping)
    if (!resolved.name || resolved.price === undefined) {
      throw new ExcelParseError(
        `Пресет «${preset.label}» не нашёл обязательные колонки (наименование и цена)`
      )
    }

    const rows: ParsedRow[] = []
    let skipped = 0
    let markedCount = 0
    for (const record of records) {
      const row = this.mapRecord(record, resolved, preset.id)
      if (!row) {
        skipped++
        continue
      }
      if (row.requiresMarking) {
        markedCount++
      }
      rows.push(row)
    }

    return { presetId: preset.id, rows, skipped, markedCount }
  }

  /** Нормализация заголовка: нижний регистр, схлопнутые пробелы. */
  static normalizeHeader(h: string): string {
    return h.trim().toLowerCase().replace(/\s+/g, ' ')
  }

  /**
   * Авто-детект пресета: выбираем тот, у которого больше всего совпавших
   * обязательных колонок (name + price), затем — по общему числу совпадений.
   */
  private detectPreset(headers: string[]): ImportPreset | undefined {
    const normalized = headers.map((h) => ExcelParser.normalizeHeader(h))
    let best: { preset: ImportPreset; score: number } | undefined

    for (const preset of this.presets) {
      const resolved = this.resolveColumns(headers, preset.mapping, normalized)
      if (!resolved.name || resolved.price === undefined) {
        continue
      }
      const score = (Object.keys(resolved) as TargetField[]).filter(
        (k) => resolved[k] !== undefined
      ).length
      if (!best || score > best.score) {
        best = { preset, score }
      }
    }
    return best?.preset
  }

  /**
   * Сопоставить целевые поля с реальными заголовками книги.
   * @returns карта targetField -> фактический заголовок колонки.
   */
  private resolveColumns(
    headers: string[],
    mapping: ColumnMapping,
    normalizedHeaders?: string[]
  ): Partial<Record<TargetField, string>> {
    const normalized =
      normalizedHeaders ?? headers.map((h) => ExcelParser.normalizeHeader(h))
    const result: Partial<Record<TargetField, string>> = {}

    for (const field of Object.keys(mapping) as TargetField[]) {
      const raw = mapping[field]
      if (!raw) continue
      const candidates = raw.map((c) => ExcelParser.normalizeHeader(c))
      const idx = normalized.findIndex((h) => candidates.includes(h))
      if (idx !== -1) {
        result[field] = headers[idx]
      }
    }
    return result
  }

  /** Преобразовать исходную запись в ParsedRow или null, если строка невалидна. */
  private mapRecord(
    record: Record<string, unknown>,
    columns: Partial<Record<TargetField, string>>,
    presetId: string
  ): ParsedRow | null {
    const name = this.readString(record, columns.name)
    const price = this.readNumber(record, columns.price)
    if (!name || price === null) {
      return null
    }
    const marking = classifyDairyMarking(name)

    // Весовой товар: явный флаг «Весовой: да/true» ИЛИ единица измерения «кг».
    const isWeightRaw = this.readString(record, columns.isWeight).toLowerCase()
    const rawUnit = this.readString(record, columns.unit).toLowerCase()
    const isWeight = isWeightRaw === 'да' || isWeightRaw === 'true' || rawUnit === 'кг'
    // Единицу измерения сохраняем как в выгрузке (источник истины). Принудительно
    // проставляем 'кг' ТОЛЬКО когда единица не указана, но товар помечен весовым —
    // так реальные данные FusionPOS (182 позиции с unit='кг') не искажаются, а
    // редкие конфликты «Весовой=да / unit=шт» не раздувают счётчик весовых.
    const unit = rawUnit || (isWeight ? 'кг' : 'шт')
    const category = this.readString(record, columns.category) || 'Общее'
    const subcategory = this.readString(record, columns.subcategory) || undefined

    // Неиспользуемые колонки источника — в namespace пресета, с лимитом 4 КБ.
    const known = new Set(Object.values(columns))
    const extra: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(record)) {
      if (known.has(key)) continue
      if (value == null || value === '') continue
      extra[key] = value
    }
    let sourceMeta: Record<string, unknown> | undefined
    if (Object.keys(extra).length > 0) {
      const candidate = { [presetId]: extra }
      if (JSON.stringify(candidate).length <= 4096) {
        sourceMeta = candidate
      }
    }

    return {
      name,
      barcode: this.readString(record, columns.barcode) || undefined,
      price,
      stock: this.readNumber(record, columns.stock) ?? undefined,
      unit,
      category,
      subcategory,
      sourceMeta,
      isWeight,
      requiresMarking: marking.requiresMarking,
      markingKeyword: marking.matchedKeyword,
      raw: record
    }
  }

  private readString(
    record: Record<string, unknown>,
    column?: string
  ): string {
    if (!column) {
      return ''
    }
    const value = record[column]
    return value == null ? '' : String(value).trim()
  }

  private readNumber(
    record: Record<string, unknown>,
    column?: string
  ): number | null {
    if (!column) {
      return null
    }
    const value = record[column]
    if (value == null || value === '') {
      return null
    }
    // Поддержка «1 234,56» (пробелы-разделители тысяч + запятая).
    const normalized = String(value)
      .replace(/\s/g, '')
      .replace(',', '.')
    const num = Number(normalized)
    return Number.isFinite(num) ? num : null
  }
}
